import React from 'react';
import { FiMail, FiPhone, FiMapPin } from 'react-icons/fi';
import ScrollReveal from "../../components/ScrollReveal";
import { useLanguage } from "../../contexts/LanguageContext";

type ContactItemProps = {
    icon: React.ReactNode;
    title: string;
    description: React.ReactNode;
};

const ContactItem: React.FC<ContactItemProps> = ({ icon, title, description }) => {
    return (
        <div className="flex flex-col items-center md:items-start text-center md:text-left mb-6 w-full max-w-sm break-words mx-auto">
            {/* Icon */}
            <div className="bg-[#83C441] p-4 rounded-full inline-flex items-center justify-center mb-4">
                {icon}
            </div>

            {/* Title */}
            <h2 className="text-xl font-[Verdana] font-bold text-white mb-2">{title}</h2>

            {/* Description */}
            <p className="text-[#ffffff]/60 text-md break-words whitespace-pre-line">{description}</p>
        </div>
    );
};



const ContactSection: React.FC = () => {
    const { t, tString } = useLanguage();
    return (
        <section
            id='contacto'
            className="w-screen  bg-[#282A2A]"
        >
            <div className="scroll-mt-40 w-screen px-16 mt-16 flex flex-col md:flex-row text-left">
                {/* Contact Info / Title */}
                <div className="flex-1 text-white">
                    <ScrollReveal direction="up" delay={0.2} duration={0.8}>
                        <h1 className="text-4xl  text-white mb-10 font-rockwell font-bold">
                            {t('contact.title')}
                        </h1>
                    </ScrollReveal>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-10">
                        <ScrollReveal direction="left" delay={0.3} duration={0.8}>
                            <ContactItem
                                icon={<FiMail className="text-white text-xl" />}
                                title={tString('contact.email')}
                                description={
                                    <>
                                        <span className="font-bold text-sm">MEX:</span>
                                        {`\n\t\tlogistica@greencubelog.com\n\t\t\tventas@montelo-express.com\n`}
                                        <span className="font-bold text-sm">USA:</span>
                                        {'\n\tlogistics@greencubelog.com'}
                                    </>
                                }
                            />
                        </ScrollReveal>

                        <ScrollReveal direction="left" delay={0.4} duration={0.8}>
                            <ContactItem
                                icon={<FiPhone className="text-white text-xl" />}
                                title={tString('contact.phone')}
                                description={
                                    <>
                                        <span className="font-bold text-sm">{tString('contact.phoneLabels.mex')}</span>
                                        {`\n\t${tString('contact.phoneLabels.office')}\t(81) 3674 3635 / 36\n\t${tString('contact.phoneLabels.yard')}\t\t(81) 8385 5768 y (81) 4162 2134\n`}
                                        <span className="font-bold text-sm">{tString('contact.phoneLabels.usa')}</span>
                                        {"\n\t\t\t(210) 233 1213"}
                                    </>
                                }
                            />
                        </ScrollReveal>

                        <ScrollReveal direction="left" delay={0.5} duration={0.8}>
                            <ContactItem
                                icon={<FiMapPin className="text-white text-xl" />}
                                title={tString('contact.address')}
                                description={
                                    <>
                                        <span className="font-bold text-sm">{tString('contact.addressLabels.corporate')}</span>
                                        {"\n\tAv. José Eleuterio González #2533 D306-309 Plaza Gonzalitos,\n\tCol. Mitras Norte, Monterrey, N.L., México C.P. 64320\n\n"}
                                        <span className="font-bold text-sm">{tString('contact.addressLabels.operational')}</span>
                                        {"\n\tGregorio Caballero #1833 Col. Andrés Caballero Moreno Agrop.\n\tGral. Escobedo, N.L. Méx. C.P. 66089."}
                                    </>
                                }
                            />
                        </ScrollReveal>
                    </div>
                </div>

                {/* Contact Form */}
                <div className="flex-1 pt-6">
                    <ScrollReveal direction="right" delay={0.6} duration={0.8}>
                        <form
                            action="https://formsubmit.co/logistica@greencubelog.com"
                            method="POST"
                            className="bg-[#ffffff]/90 p-10 rounded-lg"
                        >
                            {/* Optional hidden inputs */}
                            <input type="hidden" name="_captcha" value="true" />
                            <input type="hidden" name="_subject" value={t('contact.form.subject')} />
                            <input type="hidden" name="_next" value="https://www.gbgmx.com" />

                            <div className="mb-6">
                                <label
                                    htmlFor="email"
                                    className="block text-sm font-medium text-[#282A2A] mb-2"
                                >
                                    {t('contact.form.email')}
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    required
                                    className="w-full p-3 text-sm text-[#282A2A] bg-[#ffffff]/20 rounded-md focus:outline-none focus:ring-2 focus:ring-[#282A2A]/20 border border-[#282A2A]/20"
                                    placeholder={tString('contact.form.emailPlaceholder')}
                                />
                            </div>

                            <div className="mb-6">
                                <label
                                    htmlFor="message"
                                    className="block text-sm font-medium text-[#282A2A] mb-2"
                                >
                                    {t('contact.form.message')}
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    required
                                    className="w-full p-3 text-sm text-[#282A2A] bg-[#ffffff]/20 rounded-md focus:outline-none focus:ring-2 focus:ring-[#282A2A]/20 border border-[#282A2A]/20"
                                    placeholder={tString('contact.form.messagePlaceholder')}
                                    rows={18}
                                />
                            </div>

                            <button
                                type="submit"
                                className="w-full py-3 bg-[#83C441] font-[Verdana] font-bold text-white rounded-md hover:bg-[#83C441] focus:outline-none"
                            >
                                {t('contact.form.submit')}
                            </button>
                        </form>
                    </ScrollReveal>
                </div>
            </div>
        </section>
    );
};

export default ContactSection;
